import { useCallback, useMemo } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  changeTimeZone,
  checkTimeZoneSaveDisabled,
  deleteTimeZone,
  discardChanges,
  firstTimeZoneSelector,
  saveTimeZone,
  secondTimeZoneSelector,
  setFirstTimeZone,
  setSecondTimeZone,
  timeZoneMapSelector,
} from 'slices/timeZone.slice';
import type { TTimeZone } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import TimeZone from './TimeZone';

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const TimeZoneDrawer = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const firstTimeZone = useAppSelector(firstTimeZoneSelector);
  const secondTimeZone = useAppSelector(secondTimeZoneSelector);
  const timeZoneMap: Record<number | string, TTimeZone> =
    useAppSelector(timeZoneMapSelector);

  const setFirstResource = useCallback(
    (timeZone: TTimeZone) => dispatch(setFirstTimeZone(timeZone)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (timeZone: TTimeZone) => dispatch(setSecondTimeZone(timeZone)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    timeZoneMap,
    firstTimeZone,
    secondTimeZone,
    setFirstResource,
    setSecondResource
  );

  const firstTimeZoneSaveDisabled = useMemo(
    () => dispatch(checkTimeZoneSaveDisabled(true)),
    [firstTimeZone]
  );

  const secondTimeZoneSaveDisabled = useMemo(
    () => dispatch(checkTimeZoneSaveDisabled(false)),
    [secondTimeZone]
  );

  const onDelete = useCallback((timeZone: TTimeZone) => {
    dispatch(deleteTimeZone(timeZone.id));
  }, []);

  const onSave = useCallback((timeZone: TTimeZone) => {
    dispatch(saveTimeZone(timeZone));
  }, []);

  const onClose = useCallback(
    (isFirstDrawer: boolean) =>
      confirmDiscardPaddingChanges(isFirstDrawer, () => {
        if (isFirstDrawer) {
          dispatch(setFirstTimeZone(undefined));
        } else {
          dispatch(setSecondTimeZone(undefined));
        }
      }),
    []
  );

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: firstTimeZone?.name || '',
        content: (
          <TimeZone
            timeZone={firstTimeZone}
            onChange={({ key, value, object }) =>
              dispatch(
                changeTimeZone({ isFirstDrawer: true, key, value, object })
              )
            }
          />
        ),
        drawerOpen: !!firstTimeZone,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('time_zone_confirmation_delete')}
            handleDelete={() => onDelete(firstTimeZone!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: true,
                  id: firstTimeZone!.id,
                })
              )
            }
            handleSave={() => onSave(firstTimeZone!)}
            isSaveDisable={firstTimeZoneSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(true),
      }}
      secondDrawer={{
        title: secondTimeZone?.name || '',
        content: (
          <TimeZone
            timeZone={secondTimeZone}
            onChange={({ key, value, object }) =>
              dispatch(
                changeTimeZone({ isFirstDrawer: false, key, value, object })
              )
            }
          />
        ),
        drawerOpen: !!secondTimeZone,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('time_zone_confirmation_delete')}
            handleDelete={() => onDelete(secondTimeZone!)}
            handleDiscard={() =>
              dispatch(
                discardChanges({
                  isFirstDrawer: false,
                  id: secondTimeZone!.id,
                })
              )
            }
            handleSave={() => onSave(secondTimeZone!)}
            isSaveDisable={secondTimeZoneSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => !open && onClose(false),
      }}
    />
  );
};

export default TimeZoneDrawer;
